import { createContext, useContext, useState, useRef, useEffect, useCallback, type ReactNode } from 'react'
import { useTimeTracker } from '../hooks/useTimeTracker'
import IdleAlert from '../components/projetos/IdleAlert'

const IDLE_TIMEOUT = 15 * 60 * 1000 // 15 minutes

const TimeTrackerCtx = createContext(null)

export function TimeTrackerProvider({ children }: { children: ReactNode }) {
  const tracker = useTimeTracker()
  const { isRunning, seconds, pause } = tracker

  const [idleOpen, setIdleOpen] = useState(false)
  const idleTimerRef = useRef(null)
  const idleOpenRef = useRef(false)
  idleOpenRef.current = idleOpen

  // ── Idle detection ──────────────────────────────────────
  const resetIdleTimer = useCallback(() => {
    if (idleOpenRef.current) return // don't reset while dialog is open
    clearTimeout(idleTimerRef.current)
    idleTimerRef.current = setTimeout(() => setIdleOpen(true), IDLE_TIMEOUT)
  }, [])

  useEffect(() => {
    if (!isRunning) {
      clearTimeout(idleTimerRef.current)
      setIdleOpen(false)
      return
    }
    const events = ['mousemove', 'keydown', 'mousedown', 'scroll', 'touchstart']
    const handler = () => resetIdleTimer()
    events.forEach(e => window.addEventListener(e, handler, { passive: true }))
    resetIdleTimer()
    return () => {
      events.forEach(e => window.removeEventListener(e, handler))
      clearTimeout(idleTimerRef.current)
    }
  }, [isRunning, resetIdleTimer])

  // ── Idle actions ────────────────────────────────────────
  const handleIdleContinue = useCallback(() => {
    setIdleOpen(false)
    resetIdleTimer()
  }, [resetIdleTimer])

  const handleIdlePause = useCallback(async () => {
    setIdleOpen(false)
    await pause({ idle: true })
  }, [pause])

  const handleIdleStop = useCallback(async () => {
    setIdleOpen(false)
    await pause({ idle: true })
  }, [pause])

  return (
    <TimeTrackerCtx.Provider value={{
      ...tracker,
      idleOpen,
    }}>
      {children}
      <IdleAlert
        open={idleOpen}
        seconds={seconds}
        onContinue={handleIdleContinue}
        onPause={handleIdlePause}
        onStop={handleIdleStop}
      />
    </TimeTrackerCtx.Provider>
  )
}

export function useTimeTrackerCtx() {
  const ctx = useContext(TimeTrackerCtx)
  if (!ctx) throw new Error('useTimeTrackerCtx fora do TimeTrackerProvider')
  return ctx
}
